import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle, Archive, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface Feedback {
  id: string;
  user_email: string | null;
  rating: number;
  feedback_text: string;
  page_url: string | null;
  created_at: string;
  status: "new" | "reviewed" | "archived";
  admin_notes: string | null;
}

export const AdminFeedbackManagement = () => {
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchFeedback();
    const cleanup = setupRealtimeSubscription();
    return cleanup;
  }, []);

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`feedback_changes_${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "website_feedback",
        },
        () => {
          fetchFeedback();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from("website_feedback")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFeedbackList((data || []) as Feedback[]);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast({
        title: "Error",
        description: "Failed to load feedback.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateFeedbackStatus = async (id: string, status: "new" | "reviewed" | "archived", notes?: string) => {
    try {
      const { error } = await supabase
        .from("website_feedback")
        .update({
          status,
          admin_notes: notes || adminNotes || null,
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Feedback marked as ${status}.`,
      });

      setSelectedFeedback(null);
      setAdminNotes("");
      fetchFeedback();
    } catch (error) {
      console.error("Error updating feedback:", error);
      toast({
        title: "Error",
        description: "Failed to update feedback status.",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "reviewed":
        return "bg-green-100 text-green-800";
      case "archived":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const stats = {
    total: feedbackList.length,
    new: feedbackList.filter((f) => f.status === "new").length,
    avgRating: feedbackList.length > 0
      ? (feedbackList.reduce((sum, f) => sum + f.rating, 0) / feedbackList.length).toFixed(1)
      : "0",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">New Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold">{stats.avgRating}</div>
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback List */}
      <Card>
        <CardHeader>
          <CardTitle>Website Feedback</CardTitle>
          <CardDescription>User feedback and ratings for the website</CardDescription>
        </CardHeader>
        <CardContent>
          {feedbackList.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No feedback received yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedbackList.map((feedback) => (
                <Card key={feedback.id} className="border-2">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {/* Star Rating */}
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${
                                    star <= feedback.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                            <Badge className={getStatusColor(feedback.status)}>
                              {feedback.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {feedback.user_email || "Anonymous"} •{" "}
                            {format(new Date(feedback.created_at), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>

                      {/* Feedback Text */}
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <p className="text-sm whitespace-pre-wrap">{feedback.feedback_text}</p>
                      </div>

                      {/* Page URL */}
                      {feedback.page_url && (
                        <p className="text-xs text-muted-foreground">
                          Page: <span className="font-mono">{feedback.page_url}</span>
                        </p>
                      )}

                      {/* Admin Notes */}
                      {selectedFeedback === feedback.id ? (
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Add admin notes..."
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            rows={2}
                            className="text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => updateFeedbackStatus(feedback.id, "reviewed")}
                            >
                              Save & Mark Reviewed
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedFeedback(null);
                                setAdminNotes("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {feedback.admin_notes && (
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                              <p className="text-xs font-medium text-blue-900 mb-1">Admin Notes:</p>
                              <p className="text-sm text-blue-800">{feedback.admin_notes}</p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-2 flex-wrap">
                            {feedback.status === "new" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedFeedback(feedback.id);
                                    setAdminNotes(feedback.admin_notes || "");
                                  }}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Add Notes & Review
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateFeedbackStatus(feedback.id, "reviewed")}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Mark Reviewed
                                </Button>
                              </>
                            )}
                            {feedback.status !== "archived" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateFeedbackStatus(feedback.id, "archived")}
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
